import type {
  BaseBuildingsResponse,
  ClaimAllProductionRequest,
  ClaimProductionRequest,
  ClaimProductionResponse,
  ConstructionListResponse,
  HealthResponse,
  LoginRequest,
  MeResponse,
  ProductionJobDto,
  ProductionListResponse,
  RegisterRequest,
  ResearchListResponse,
  ServerBrowserResponse,
  SpeedupBuildingRequest,
  SpeedupBuildingResponse,
  SpeedupProductionRequest,
  SpeedupProductionResponse,
  SpeedupResearchRequest,
  SpeedupResearchResponse,
  StartProductionRequest,
  StartResearchRequest,
  StartResearchResponse,
  UpgradeBuildingRequest,
  UpgradeBuildingResponse,
} from "@stahlfront/shared";

const BASE = import.meta.env.VITE_API_BASE ?? "/api";

async function jsonRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`${path} ${r.status}: ${text || "no body"}`);
  }
  return (await r.json()) as T;
}

export async function fetchHealth(): Promise<HealthResponse> {
  return jsonRequest<HealthResponse>("/health");
}

export async function fetchServerBrowser(): Promise<ServerBrowserResponse> {
  return jsonRequest<ServerBrowserResponse>("/server-browser");
}

export interface AuthOk {
  readonly ok: true;
  readonly playerId: string;
}

export async function register(req: RegisterRequest): Promise<AuthOk> {
  return jsonRequest<AuthOk>("/auth/register", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export async function login(req: LoginRequest): Promise<AuthOk> {
  return jsonRequest<AuthOk>("/auth/login", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export async function logout(): Promise<{ ok: true }> {
  return jsonRequest<{ ok: true }>("/auth/logout", { method: "POST" });
}

export async function getMe(): Promise<MeResponse | null> {
  try {
    return await jsonRequest<MeResponse>("/auth/me");
  } catch (err) {
    if (String(err).includes(" 401")) return null;
    throw err;
  }
}

/* ── Phase 1.C: building roster ───────────────────────────────────────────── */

export async function fetchBaseBuildings(baseId: string): Promise<BaseBuildingsResponse> {
  return jsonRequest<BaseBuildingsResponse>(`/base/${baseId}/buildings`);
}

/* ── Phase 1.E + 2.A + 2.D: production endpoints ─────────────────────────── */

export async function fetchProductionList(baseId: string): Promise<ProductionListResponse> {
  return jsonRequest<ProductionListResponse>(`/production/list?baseId=${encodeURIComponent(baseId)}`);
}

export async function startProduction(
  body: StartProductionRequest,
): Promise<{ readonly ok: true; readonly job: ProductionJobDto }> {
  return jsonRequest("/production/start", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Phase 2.D — diamond-charged speed-up; response carries refreshed wallet + actual cost. */
export async function speedupProduction(
  body: SpeedupProductionRequest,
): Promise<SpeedupProductionResponse> {
  return jsonRequest("/production/speedup", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function claimProduction(
  body: ClaimProductionRequest,
): Promise<ClaimProductionResponse> {
  return jsonRequest("/production/claim", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function claimAllProduction(
  body: ClaimAllProductionRequest,
): Promise<ClaimProductionResponse> {
  return jsonRequest("/production/claim-all", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/* ── Phase 2.C + 2.D: building upgrade + speed-up ─────────────────────────── */

export async function upgradeBuilding(
  body: UpgradeBuildingRequest,
): Promise<UpgradeBuildingResponse> {
  return jsonRequest("/buildings/upgrade", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Phase 2.D — diamond-charged speed-up for an in-flight building upgrade. */
export async function speedupBuilding(
  body: SpeedupBuildingRequest,
): Promise<SpeedupBuildingResponse> {
  return jsonRequest("/buildings/speedup", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function fetchConstructionList(baseId: string): Promise<ConstructionListResponse> {
  return jsonRequest<ConstructionListResponse>(
    `/buildings/construction/list?baseId=${encodeURIComponent(baseId)}`,
  );
}

/* ── Phase 2.F.1: research endpoints ─────────────────────────────────────── */

export async function fetchResearchList(): Promise<ResearchListResponse> {
  return jsonRequest<ResearchListResponse>("/research/list");
}

export async function startResearch(body: StartResearchRequest): Promise<StartResearchResponse> {
  return jsonRequest("/research/start", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function speedupResearch(
  body: SpeedupResearchRequest,
): Promise<SpeedupResearchResponse> {
  return jsonRequest("/research/speedup", {
    method: "POST",
    body: JSON.stringify(body),
  });
}
